import { apiService } from './apiService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.eternal-health.com/api';

export type InsightType = 'daily_recap' | 'supplement_lapse' | string;
export type ReviewStatus = 'unreviewed' | 'correct' | 'incorrect' | 'not_useful';

export interface InsightPatient {
  patient_id: number;
  name: string | null;
  gender: string | null;
  age: number | null;
  weight: number | null;
  RMR_kcal_day: number | null;
  timezone: string | null;
}

export interface Insight {
  id: number;
  type: InsightType;
  summary: string;
  // payload shape varies by insight type; consumers read known keys defensively.
  payload: Record<string, any>;
  confidence: number | null;
  sample_size: number | null;
  score: number | null;
  review_status: ReviewStatus;
  review_note: string | null;
  reviewed_at: string | null;
  created_at: string;
  patient: InsightPatient;
  disclaimer: string | null;
}

export interface InsightListResponse {
  count: number;
  perpage: number;
  actualPage: number;
  totalPages: number;
  results: Insight[];
}

export interface InsightFilters {
  // default 'unreviewed' on the backend; pass 'all' to include reviewed.
  review_status?: ReviewStatus | 'all';
  type?: InsightType;
  patient_id?: number;
  perpage?: number; // <= 500
  page?: number;
}

const buildQuery = (filters?: InsightFilters): string => {
  const params = new URLSearchParams();
  if (filters?.review_status) params.append('review_status', filters.review_status);
  if (filters?.type) params.append('type', filters.type);
  if (filters?.patient_id != null) params.append('patient_id', String(filters.patient_id));
  if (filters?.perpage != null) params.append('perpage', String(filters.perpage));
  if (filters?.page != null) params.append('page', String(filters.page));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
};

export const insightsService = {
  // List insights across all of the clinician's patients.
  getInsights: async (filters?: InsightFilters): Promise<InsightListResponse> => {
    const url = `${API_BASE_URL}/clinic/insights/${buildQuery(filters)}`;
    return await apiService.get(url);
  },

  // List insights scoped to a single patient. `patientId` is the patient profile
  // id — the same id used in the patient detail URL route.
  getPatientInsights: async (
    patientId: string | number,
    filters?: InsightFilters
  ): Promise<InsightListResponse> => {
    const url = `${API_BASE_URL}/clinic/patients/${patientId}/insights/${buildQuery(filters)}`;
    return await apiService.get(url);
  },

  // Mark an insight as reviewed. Returns the updated finding.
  reviewInsight: async (
    id: number,
    reviewStatus: Exclude<ReviewStatus, 'unreviewed'>,
    note?: string
  ): Promise<Insight> => {
    const url = `${API_BASE_URL}/clinic/insights/${id}/review/`;
    return await apiService.post(url, { review_status: reviewStatus, ...(note ? { note } : {}) });
  },
};
